import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  Paper,
  Divider,
  RadioGroup,
  CircularProgress,
} from "@mui/material";
import SettingsIcon from "@mui/icons-material/Settings";
import ATSSelectionCard from "./ATSSelectionCard";
import InterviewHelpATSConfig from "./InterviewHelpATSConfig";
import ClientProvidedATSConfig from "./ClientProvidedATSConfig";
import { useSelector } from "react-redux";
import { RootState } from "store";
import { mergeIntegrationService } from "services/mergeIntegration";

export interface MergeDataProps {
  enableSync: boolean;
  enableJobSync?: boolean;
  lastUpdated: string | number;
  integration?: string;
}

const ATSIntegrationSettings = () => {
  const expert = useSelector((state: RootState) => state.auth.user);
  const expertId = expert.expertId;

  const [selectedATS, setSelectedATS] = useState("");
  const [loading, setLoading] = useState(false);
  const [mergeSetting, setMergeSetting] = useState<any>(null);
  const [activeATS, setActiveATS] = useState("");

  useEffect(() => {
    if (expertId) {
      getMergeSettings(expertId);
    }
  }, [expertId]);

  const getMergeSettings = async (expertId: string) => {
    try {
      setLoading(true);
      const response = await mergeIntegrationService.getMergeSetting({ expertId });
      setMergeSetting(response);

      const hasIntegration = response?.output?.has_integration;
      const keyType = response?.output?.merge_integration?.key_type;

      if (hasIntegration) {
        const atsType =
          keyType === "client" ? "Client-Provided ATS" : "InterviewHelp ATS";
        setSelectedATS(atsType);
        setActiveATS(atsType);
      } else {
        setSelectedATS("");
      }
    } catch (error) {
      console.error("Failed to fetch merge settings:", error);
      setSelectedATS("");
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <Box
        p={4}
        display="flex"
        justifyContent="center"
        alignItems="center"
        height="60vh"
      >
        <CircularProgress />
      </Box>
    );
  }

  const mergeData = mergeSetting?.output?.merge_integration
    ? {
      enableSync: mergeSetting.output.merge_integration.enable_sync,
      lastUpdated: mergeSetting.output.merge_integration.last_updated,
      integration: mergeSetting.output.merge_integration.integration,
      enableJobSync: mergeSetting.output.merge_integration.enable_job_sync,
    }
    : null;


  const ATS_OPTIONS = [
    {
      value: "InterviewHelp ATS",
      title: "InterviewHelp ATS",
      description: "Use our built-in ATS solution with seamless integration",
    },
    {
      value: "Client-Provided ATS",
      title: "Client-Provided ATS",
      description: "Connect your existing ATS system via Merge integration",
    },
  ];

  return (
    <Box p={4}>
      {/* Header */}
      <Box display="flex" alignItems="center" mb={2}>
        <SettingsIcon color="primary" sx={{ mr: 1 }} />
        <Typography variant="h5" fontWeight={600}>
          ATS Integration Settings
        </Typography>
      </Box>

      <Typography color="text.secondary" mb={4}>
        Configure your Applicant Tracking System integration to sync candidate
        and job data seamlessly.
      </Typography>

      {/* Section title */}
      <Typography variant="h6" fontWeight={600} mb={1}>
        ATS Integration
      </Typography>
      <Typography color="text.secondary" mb={3}>
        Configure your Applicant Tracking System integration and manage data
        synchronization.
      </Typography>

      {/* Selection cards */}
      <Paper variant="outlined" sx={{ p: 3, borderRadius: 2 }}>
        <Typography variant="subtitle1" fontWeight={600} mb={1}>
          Select ATS Type
        </Typography>
        <Typography color="text.secondary" mb={2}>
          Choose how you want to integrate your Applicant Tracking System
        </Typography>

        <Divider sx={{ mb: 2 }} />
        <RadioGroup
          value={selectedATS}
          onChange={(e) => setSelectedATS(e.target.value)}
        >
          {(activeATS ? ATS_OPTIONS.filter(opt => opt.value === activeATS) : ATS_OPTIONS)
            .map(({ value, title, description }) => (
              <ATSSelectionCard
                key={value}
                value={value}
                title={title}
                description={description}
                selected={selectedATS === value}
                onSelect={setSelectedATS}
              />
            ))}
        </RadioGroup>
      </Paper>

      {/* Dynamic sections */}
      {selectedATS === "InterviewHelp ATS" && (
        <InterviewHelpATSConfig expertId={expertId} mergeData={mergeData} setActiveATS={setActiveATS} activeATS={activeATS} onSettingsUpdated={() => getMergeSettings(expertId)} />
      )}
      {selectedATS === "Client-Provided ATS" && (
        <ClientProvidedATSConfig
          expertId={expertId}
          mergeData={mergeData}
          activeStatus={activeATS === "Client-Provided ATS"}
          activeATS={activeATS}
          setActiveATS={setActiveATS}
          onSettingsUpdated={() => getMergeSettings(expertId)}
        />
      )}
    </Box>
  );
};

export default ATSIntegrationSettings;
